import type {PayloadAction} from '@reduxjs/toolkit'

import {runCustomBuildAction} from '../components/common/RunBuild/RunBuild.actions'
import {projectTab} from '../components/pages/ProjectPage/ProjectPage.slices'
import type {State} from '../reducers/types'
import request from '../rest/request'
import {getClientId, getExtensionEndpoint} from '../selectors'
import {restApi} from '../services/rest'
import {buildTab, buildTypeTab, isSakuraUI, showQueuedBuildsPerBranch} from '../slices'
import type {UrlExtension} from '../types'
import {base_uri, BS} from '../types/BS_types'

import measureWebVitals from './performance'
import {startRouterListening} from './routes.tracking'
import type {FusAction, FusEvent} from './statistics.types'
import {addActionsToQueue, defaultSendStatThrottleTime, UIStatistics} from './tracking'
import {
  ACTION_PAGES_GROUP,
  ACTION_PAGES_RECORDER_VERSION,
  ACTION_PAGES_VERSION,
  generateFUSEvent,
  retrieveLength,
  retrieveTabContext,
} from './utils'

const scheduleSendState = (extension: UrlExtension<any>, state: State) =>
  window.setTimeout(() => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    prepareAndSendStat(extension, state)
  }, UIStatistics.interval)

const prepareData = (state: State): FusEvent => {
  const actions = UIStatistics.actionQueue
  const realMode = UIStatistics.mode === 'real'
  const devMode = process.env.NODE_ENV === 'development'
  return {
    internalMode: devMode || !realMode,
    user: getClientId(state),
    buildNumber: '61100',
    actions,
  }
}

const prepareAndSendStat = (extension: UrlExtension<any>, state: State) => {
  if (UIStatistics.send && UIStatistics.actionQueue.length > 0) {
    const actions = UIStatistics.actionQueue
    const stat = prepareData(state)
    UIStatistics.actionQueue = []

    try {
      sendData(extension.serverUrl ?? base_uri, extension.endpoint, stat)
      scheduleSendState(extension, state)
    } catch (error) {
      BS?.Log?.error(error)
      UIStatistics.actionQueue = UIStatistics.actionQueue.concat(actions)
      scheduleSendState(extension, state)
    }
  } else {
    scheduleSendState(extension, state)
  }
}

function processUIStatisticsOptions(state: State) {
  if (!UIStatistics.send) {
    const extension = getExtensionEndpoint(state, 'ui-statistics')
    const sendStat = extension?.endpoint
    const sendDisabled = extension?.options?.disabled

    if (extension && sendStat != null && !sendDisabled) {
      UIStatistics.send = true
      UIStatistics.mode =
        extension.options && extension.options.mode === 'real' ? 'real' : 'internal'
      UIStatistics.interval = extension.options?.interval
        ? extension.options.interval
        : defaultSendStatThrottleTime
      scheduleSendState(extension, state)
    }
  }
}

export function track(state: State, action: PayloadAction<unknown>) {
  const actions = actionsToSend(action)
  addActionsToQueue(actions)
  processUIStatisticsOptions(state)
}
const withReferrer = true
const withContext = true

function actionsToSend(
  action: PayloadAction<unknown>,
): ReadonlyArray<FusAction> | null | undefined {
  if (
    restApi.endpoints.getAllProjectsNormalized.matchFulfilled(action) &&
    action.meta.arg.originalArgs.isOverview
  ) {
    const payload = {
      projects: `${
        action.payload?.entities?.overviewProjects
          ? retrieveLength(action.payload.entities.overviewProjects)
          : -1
      }`,
      buildTypes: `${
        action.payload?.entities?.overviewBuildTypes
          ? retrieveLength(action.payload.entities.overviewBuildTypes)
          : -1
      }`,
    }
    return [generateFUSEvent('fetchOverview', payload)]
  }
  if (isSakuraUI.actions.set.match(action)) {
    const event = generateFUSEvent(action.type, null, {
      withReferrer,
      withContext,
    })
    return [
      {
        ...event,
        action: 'view',
        group: ACTION_PAGES_GROUP,
        groupVersion: ACTION_PAGES_VERSION,
        recorderVersion: ACTION_PAGES_RECORDER_VERSION,
      },
      event,
    ]
  }
  if (runCustomBuildAction.fulfilled.match(action)) {
    const {branch} = action.meta.arg
    const defaultBranch = branch?.default === true || branch?.groupFlag === true
    return [
      generateFUSEvent(
        action.type,
        {
          custom: 'true',
          error: 'false',
          defaultBranch: defaultBranch.toString(),
        },
        {
          withContext,
        },
      ),
    ]
  }
  if (runCustomBuildAction.rejected.match(action)) {
    const {branch} = action.meta.arg
    const defaultBranch = branch?.default === true || branch?.groupFlag === true
    return [
      generateFUSEvent(
        action.type,
        {
          custom: 'true',
          error: 'true',
          defaultBranch: defaultBranch.toString(),
        },
        {
          withContext,
        },
      ),
    ]
  }
  if (restApi.endpoints.runBuild.matchFulfilled(action)) {
    const {branch} = action.meta.arg.originalArgs
    const defaultBranch = branch?.default === true || branch?.groupFlag === true
    return [
      generateFUSEvent(
        action.type,
        {
          custom: 'false',
          error: 'false',
          defaultBranch: defaultBranch.toString(),
        },
        {
          withContext,
        },
      ),
    ]
  }
  if (restApi.endpoints.runBuild.matchRejected(action)) {
    if (action.meta.condition) {
      // request was aborted due to condition (another query already running)
      return null
    }
    const {branch} = action.meta.arg.originalArgs
    const defaultBranch = branch?.default === true || branch?.groupFlag === true
    return [
      generateFUSEvent(
        action.type,
        {
          custom: 'false',
          error: 'true',
          defaultBranch: defaultBranch.toString(),
        },
        {
          withContext,
        },
      ),
    ]
  }
  if (restApi.endpoints.getTabs.matchFulfilled(action)) {
    return [
      generateFUSEvent(action.type, null, {
        withContext,
      }),
    ]
  }
  if (
    buildTypeTab.actions.change.match(action) ||
    buildTab.actions.change.match(action) ||
    projectTab.actions.change.match(action)
  ) {
    return [
      generateFUSEvent(
        action.type,
        {
          next: retrieveTabContext(action.payload),
        },
        {
          withContext,
        },
      ),
    ]
  }
  if (
    showQueuedBuildsPerBranch.actions.hide.match(action) ||
    showQueuedBuildsPerBranch.actions.show.match(action) ||
    restApi.endpoints.getCleanupRules.matchPending(action) ||
    restApi.endpoints.createCleanupRule.matchPending(action) ||
    restApi.endpoints.updateCleanupRule.matchPending(action) ||
    restApi.endpoints.deleteCleanupRule.matchPending(action) ||
    restApi.endpoints.getCleanupPolicies.matchPending(action) ||
    restApi.endpoints.updateCleanupPolicy.matchPending(action) ||
    restApi.endpoints.resetCleanupPolicy.matchPending(action)
  ) {
    return [generateFUSEvent(action.type, null)]
  }
  return null
}

function sendDataWithFetch(serverUrl: string, endpoint: string, data: any): void {
  request(serverUrl, `${endpoint}`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

function sendData(serverUrl: string, endpoint: string | null | undefined, data: any): void {
  if (endpoint == null) {
    return
  }

  const csrfToken = BS?.CSRF?.getCachedToken() || ''
  const formData = new FormData()
  formData.append('tc-csrf-token', csrfToken)
  formData.append('data', JSON.stringify(data))

  if (typeof window.navigator.sendBeacon === 'function') {
    window.navigator.sendBeacon(`${serverUrl}${endpoint}`, formData)
  } else {
    sendDataWithFetch(serverUrl, endpoint, data)
  }
}

window.addEventListener('beforeunload', () => {
  if (UIStatistics.send && UIStatistics.actionQueue.length > 0) {
    addActionsToQueue([
      generateFUSEvent(
        'BEACON_SENT',
        {},
        {
          withContext,
        },
      ),
    ])
    const state = window.ReactUI.store.getState()
    const stat = prepareData(state)
    const extension: UrlExtension<any> | null | undefined = getExtensionEndpoint(
      state,
      'ui-statistics',
    )
    sendData(extension?.serverUrl ?? base_uri, extension?.endpoint, stat)
  }
})
startRouterListening()
measureWebVitals()
